import { ExternalLink, Newspaper } from 'lucide-react';

import { isSafeUrl } from '@/lib/utils';

export interface AdverseMediaEvidenceMetadata {
  articleTitle?: string;
  articleSnippet?: string;
  articleDate?: string;
  articleDomain?: string;
  confidence?: number;
  inferredSeverity?: string;
  pdfPath?: string;
  filename?: string;
}

export interface AdverseMediaEvidenceItem {
  filename?: string;
  url?: string;
  evidenceId?: string;
  source?: string;
  content?: string;
  metadata?: AdverseMediaEvidenceMetadata;
}

export interface AdverseMediaFilteredItem {
  url?: string;
  title?: string;
  source?: string;
  filterReason?: string;
}

interface AdverseMediaSectionProps {
  evidence?: AdverseMediaEvidenceItem[];
  filteredResults?: AdverseMediaFilteredItem[];
  urls?: string[];
}

const FILTER_REASON_LABELS: Record<string, string> = {
  NON_ADVERSE: 'No adverse content',
  AGE_MISMATCH: 'Different age',
  LOCATION_MISMATCH: 'Different location',
  NATIONALITY_MISMATCH: 'Different nationality',
  IDENTITY_MISMATCH: 'Different person',
  EXTRACTION_FAILED: 'Could not retrieve article content',
  DUPLICATE: 'Duplicate of another article',
};

function describeFilterReason(reason?: string): string {
  if (!reason) return 'Reviewed — not relevant';
  return FILTER_REASON_LABELS[reason] ?? `Reviewed — ${reason.toLowerCase().replace(/_/g, ' ')}`;
}

export function AdverseMediaSection({ evidence, filteredResults, urls }: AdverseMediaSectionProps) {
  const hasRichEvidence = evidence && evidence.length > 0;
  const hasFilteredResults = filteredResults && filteredResults.length > 0;
  const hasPlainUrls = urls && urls.length > 0;

  if (!hasRichEvidence && !hasFilteredResults && !hasPlainUrls) return null;

  // Show rich article metadata when available
  if (hasRichEvidence || hasFilteredResults) {
    const relevantUrls = new Set(
      (evidence ?? []).map((e) => e.url).filter((u): u is string => Boolean(u)),
    );
    // Filter dropped items to ones not already present in evidence (defensive
    // — backend shouldn't ever overlap them, but render correctness matters).
    const reviewedButNotRelevant = (filteredResults ?? []).filter(
      (f) => f.url && isSafeUrl(f.url) && !relevantUrls.has(f.url),
    );

    return (
      <section className="mb-8 rounded-lg border bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-lg font-semibold text-[#051923]">Adverse Media Findings</h2>
        {hasRichEvidence && (
          <>
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-700">
              Relevant articles{' '}
              <span className="ml-1 text-xs font-normal text-gray-500">({evidence!.length})</span>
            </h3>
            <div className="mb-6 space-y-2">
              {evidence!.map((item, index) => {
                const title = item.metadata?.articleTitle;
                const date = item.metadata?.articleDate;
                const domain = item.metadata?.articleDomain;
                const url = item.url;

                if (!isSafeUrl(url)) return null;

                return (
                  <div
                    key={item.evidenceId || `evidence-${index}`}
                    className="flex items-start justify-between gap-4 py-2"
                  >
                    <div className="flex min-w-0 flex-1 items-start gap-2">
                      <Newspaper className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" />
                      <div className="min-w-0 flex-1">
                        <span className="block break-words text-sm">{title || domain || url}</span>
                        {(domain || date) && (
                          <span className="block text-xs text-gray-500">
                            {domain && date ? `${domain} • ${date}` : domain || date}
                          </span>
                        )}
                      </div>
                    </div>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex shrink-0 items-center gap-1 p-2 text-sm text-blue-600 hover:underline"
                    >
                      <ExternalLink className="h-5 w-5" />
                    </a>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {reviewedButNotRelevant.length > 0 && (
          <>
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-700">
              Reviewed — not relevant{' '}
              <span className="ml-1 text-xs font-normal text-gray-500">
                ({reviewedButNotRelevant.length})
              </span>
            </h3>
            <p className="mb-3 text-xs text-gray-500">
              Articles that passed initial filtering but were excluded from findings after deeper
              analysis. PDF snapshots are still captured and available in the evidence pack.
            </p>
            <div className="space-y-2">
              {reviewedButNotRelevant.map((item) => {
                const url = item.url!;
                const title = item.title || item.source || url;
                return (
                  <div key={url} className="flex items-start justify-between gap-4 py-2">
                    <div className="flex min-w-0 flex-1 items-start gap-2">
                      <Newspaper className="mt-0.5 h-5 w-5 shrink-0 text-gray-300" />
                      <div className="min-w-0 flex-1">
                        <span className="block break-words text-sm text-gray-700">{title}</span>
                        <span className="block text-xs text-gray-500">
                          {item.source ? `${item.source} • ` : ''}
                          {describeFilterReason(item.filterReason)}
                        </span>
                      </div>
                    </div>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex shrink-0 items-center gap-1 p-2 text-sm text-blue-600 hover:underline"
                    >
                      <ExternalLink className="h-5 w-5" />
                    </a>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </section>
    );
  }

  // Fallback: plain URLs when no rich evidence metadata is available
  return (
    <section className="mb-8 rounded-lg border bg-white p-6 shadow-sm">
      <h2 className="mb-4 text-lg font-semibold text-[#051923]">Sources</h2>
      <ul className="space-y-2">
        {urls!
          .filter((url) => isSafeUrl(url))
          .map((url) => (
            <li key={url} className="min-w-0">
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 p-2 text-sm text-blue-600 hover:underline"
              >
                <ExternalLink className="mt-0.5 h-5 w-5 shrink-0" />
                <span className="break-all">{url}</span>
              </a>
            </li>
          ))}
      </ul>
    </section>
  );
}
